import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

export default function ItemForm({ item, onSuccess, onCancel }) {
  const { getToken } = useAuth();
  const isEditMode = !!item;
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [images, setImages] = useState([]); // Array of { file, preview, url }
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    stock: '5',
  });

  // Initialize form and images from item if editing
  useEffect(() => {
    if (item) {
      setForm({
        title: item.title || '',
        description: item.description || '',
        price: item.price?.toString() || '',
        stock: item.stockCount?.toString() || '5',
      });
      // Pre-populate images with existing URLs
      if (item.imageUrls && item.imageUrls.length > 0) {
        setImages(item.imageUrls.map(url => ({
          file: null,
          preview: url,
          url: url, // Already uploaded
        })));
      }
      // Pre-select tags
      if (item.tagIds && item.tagIds.length > 0) {
        setSelectedTagIds(item.tagIds);
      }
    }
  }, [item]);

  // Fetch tags on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTags(true);
      try {
        const res = await api('/api/tags');
        if (!res.ok) {
          throw new Error('Failed to load tags');
        }
        const data = await res.json();
        if (!cancelled) {
          setTags(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoadingTags(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Scroll error message into view when an error occurs (works in modal or full page)
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - images.length);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImages(prev => [...prev, {
            file,
            preview: reader.result,
            url: null, // Will be set after upload
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleTag = (tagId) => {
    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = await getToken();
    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const url = `${baseUrl}/api/images/upload`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to upload image');
    }

    const data = await res.json();
    return data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate form
      if (!form.title.trim()) {
        throw new Error('Title is required');
      }
      if (!form.price || parseFloat(form.price) <= 0) {
        throw new Error('Price must be greater than 0');
      }
      const stock = parseInt(form.stock, 10);
      if (isNaN(stock) || stock < 0) {
        throw new Error('Stock count must be a non-negative number');
      }
      if (images.length === 0) {
        throw new Error('At least one image is required');
      }
      if (selectedTagIds.length === 0) {
        throw new Error('At least one tag is required');
      }

      // Upload images first
      const imageUrls = [];
      for (const img of images) {
        if (img.url) {
          // Already uploaded (existing image)
          imageUrls.push(img.url);
        } else {
          // Upload now (new image)
          const url = await uploadImage(img.file);
          imageUrls.push(url);
        }
      }

      // POST (create) or PUT (update) item with URLs and tag ids
      const endpoint = isEditMode ? `/api/items/${item.id}` : '/api/items/create';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const res = await api(endpoint, {
        method,
        getToken,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim() || null,
          price: parseFloat(form.price),
          stockCount: stock,
          imageUrls,
          tagIds: selectedTagIds,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }

      // Success
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold">{isEditMode ? 'Edit Item' : 'Create Item'}</h1>

      {error && (
        <p 
          ref={errorRef}
          className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg"
        >
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Title */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Title *</span>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="Item title"
            required
            disabled={loading}
          />
        </label>

        {/* Description */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Description</span>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={6}
            className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-4 text-lg resize-y"
            placeholder="Item description"
            disabled={loading}
          />
        </label>

        {/* Price */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Price ($) *</span>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            step="0.01"
            min="0.01"
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="0.00"
            required
            disabled={loading}
          />
        </label>

        {/* Stock */}
        <label className="flex flex-col gap-2">
          <span className="text-lg font-medium">Stock Count *</span>
          <input
            type="number"
            name="stock"
            value={form.stock}
            onChange={handleChange}
            min="0"
            className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
            placeholder="5"
            required
            disabled={loading}
          />
        </label>

        {/* Image Upload */}
        <div className="flex flex-col gap-2">
          <span className="text-lg font-medium">Images (up to 3) *</span>
          {images.length < 3 && (
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                disabled={loading}
                multiple={images.length === 0}
              />
              <div className="h-32 w-full rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center text-lg text-slate-600 dark:text-slate-400 hover:border-indigo-500 transition-colors">
                + Add Image {images.length > 0 && `(${images.length}/3)`}
              </div>
            </label>
          )}

          {/* Image Carousel Preview */}
          {images.length > 0 && (
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
              {images.map((img, index) => (
                <div key={index} className="flex-shrink-0 relative snap-start">
                  <div className="w-40 h-40 rounded-xl overflow-hidden border-2 border-slate-300 dark:border-slate-600 shadow-md">
                    <img
                      src={img.preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center text-xl font-bold hover:bg-red-600 active:bg-red-700 transition-colors shadow-lg"
                    disabled={loading}
                    aria-label={`Remove image ${index + 1}`}
                  >
                    ×
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-1">
                    {index + 1} / {images.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tags Multi-select */}
        <div className="flex flex-col gap-2">
          <span className="text-lg font-medium">Tags (select at least one) *</span>
          {loadingTags ? (
            <p className="text-slate-600 dark:text-slate-400">Loading tags...</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`px-4 py-2 rounded-full text-base font-medium transition-colors ${
                    selectedTagIds.includes(tag.id)
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                  disabled={loading}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col gap-4">
          <button
            type="submit"
            disabled={loading || loadingTags}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {loading ? (isEditMode ? 'Updating…' : 'Creating…') : (isEditMode ? 'Update Item' : 'Create Item')}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="h-16 text-xl w-full rounded-xl font-medium bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
